import { State } from "@neru/base/components";
import {
  useAuth,
  useCurrentAccount,
  useListControls,
  useValidSession,
} from "@neru/base/hooks";
import type { FilterParams } from "@neru/base/types";
import {
  Badge,
  Button,
  Column,
  Grid,
  Phrase,
  Row,
  Spacer,
  Switch,
} from "@neru/ui";

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Filter } from "./Filter/Filter";
import { type GetProductsResponse, useGetProductsQuery } from "./hooks";
import { ProductCard } from "./ProductCard";

type Product = GetProductsResponse["data"]["Products"][number];

const DEFAULT_PAGE_SIZE = 100; // Temporary. Until pagination is added.
export const SHOW_UNPUBLISHED_PRODUCTS_KEY = "vch.show_unpublished_products";

const Home = () => {
  const navigate = useNavigate();
  const { accountId } = useAuth();
  const { canPublishProducts } = useCurrentAccount();

  const showPrivateSetting = localStorage.getItem(
    `${SHOW_UNPUBLISHED_PRODUCTS_KEY}_${accountId}`,
  );

  const { handleFilter, listState } = useListControls({
    orderBy: [{ priority: "desc" }, { created_at: "desc" }],
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [showPrivate, setShowPrivate] = useState(
    showPrivateSetting !== "false",
  );

  const { data, error, loading, total } = useGetProductsQuery(
    listState,
    showPrivate,
  );

  const hasUnpublishedProducts = (data?.Products ?? []).some(
    (product: Product) => product.isLive === false,
  );

  const hasPrivateProducts = (data?.Products ?? []).some(
    (product: Product) => product.isPublic === false,
  );

  const onToggle = useCallback(() => {
    localStorage.setItem(
      `${SHOW_UNPUBLISHED_PRODUCTS_KEY}_${accountId}`,
      String(!showPrivate),
    );

    setShowPrivate(!showPrivate);
  }, [accountId, showPrivate]);

  useEffect(() => {
    setShowPrivate(showPrivateSetting !== "false");
  }, [accountId, showPrivateSetting]);

  const onPublishProduct = () => {
    navigate("/publish");
  };

  const onFilter = useCallback(
    (filterParams: FilterParams) => handleFilter({ ...filterParams }),
    [handleFilter],
  );

  return (
    <Row className="pb-8">
      <Column className="mb-8 grow">
        <Filter hasProductsListingError={Boolean(error)} onFilter={onFilter}>
          <Row
            className={`
              flex-wrap items-center px-2

              md:!px-8

              sm:!px-6
            `}
          >
            <Column className={`mb-2 mt-1 grow-0 justify-center pl-8`}>
              <Row
                className={`
                  mr-2 mt-1

                  lg:!ml-16

                  md:!ml-8

                  sm:!ml-2
                `}
              >
                {error ? null : (
                  <Phrase className="whitespace-nowrap">
                    All Products
                    <Spacer size="xs" />
                    <Badge
                      data-testid="total-products"
                      layout="soft"
                      shape="pill"
                    >
                      {total || "..."}
                    </Badge>
                  </Phrase>
                )}
              </Row>
            </Column>

            {useValidSession() ? (
              <>
                {(hasUnpublishedProducts ||
                  hasPrivateProducts ||
                  showPrivateSetting !== null) && (
                  <Column className="my-1 grow-0 items-center">
                    <Row className="ml-2 flex-nowrap items-center">
                      {error ? null : (
                        <>
                          <Phrase className="text-primary-200">|</Phrase>
                          <Spacer size="md" />
                        </>
                      )}
                      <Row className="items-center">
                        <Switch
                          checked={showPrivate}
                          data-testid="toggle-show-private-products"
                          label={
                            showPrivate
                              ? "Hide private and unlisted products"
                              : "Show private and unlisted products"
                          }
                          onChange={onToggle}
                          size="condensed"
                        />
                      </Row>
                    </Row>
                  </Column>
                )}
                {canPublishProducts && (
                  <Column
                    className={`
                      mb-2 mt-1 grow pr-1

                      lg:!pr-8

                      md:!pr-6

                      sm:!pr-2
                    `}
                  >
                    <Row
                      className={`
                        ml-8 justify-end pr-4

                        lg:!pr-16

                        md:!pr-10

                        sm:!pr-8
                      `}
                    >
                      <Button
                        data-testid="publish-new-product"
                        icon="wand-line"
                        label="Publish new product"
                        onClick={onPublishProduct}
                        shape="pill"
                        type="button"
                      />
                    </Row>
                  </Column>
                )}
              </>
            ) : null}
          </Row>
        </Filter>
        <Row
          className={`
            mt-6 px-2

            lg:!px-8

            md:!px-6

            sm:!px-4
          `}
        >
          <Column
            className={`
              mx-2 grow px-2

              lg:!mx-8 lg:!px-8

              md:!mx-6 md:!px-6

              sm:!mx-4 sm:!px-4
            `}
          >
            <section role="main">
              <State
                emptyStateMessage={
                  !listState.where || Object.keys(listState.where).length === 0
                    ? "There are no products in Cloud Runtime yet"
                    : "No products found"
                }
                errorIcon="plug-line"
                errorMessage={
                  error &&
                  "We're unable to load the product catalog at the moment. Please try refreshing the page or check back shortly."
                }
                isData={Boolean(data.Products.length)}
                loading={loading}
              >
                <Grid
                  className={`
                    mx-2

                    lg:!mx-8

                    md:!mx-6

                    sm:!mx-4
                  `}
                  columns={4}
                >
                  {data.Products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </Grid>
              </State>
            </section>
          </Column>
        </Row>
      </Column>
    </Row>
  );
};

export default Home;
